import React, { useState } from "react";
import { useCompleteProfile } from "@/context/CompleteProfileContext";
import CompleteProfileFooter from "../CompleteProfileFooter";
import { Camera, ImagePlus } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import OptimizedPhotoUploader from "@/components/onboarding/OptimizedPhotoUploader";
import PhotoGallery from "@/components/onboarding/PhotoGallery";
import PhotoUploadButton from "@/components/onboarding/PhotoUploadButton";

const MINIMUM_PHOTOS = 3;

const PhotosCompleteStep = () => {
  const { data, updateData, nextStep, saving } = useCompleteProfile();
  const { profileData } = useAuth();

  const photos = data.profile_photos || profileData?.profile_photos || [];
  const photoCount = photos.length;

  // State for upload button props from OptimizedPhotoUploader
  const [uploadButtonProps, setUploadButtonProps] = useState<{
    onClick: () => void;
    disabled: boolean;
  } | null>(null);

  // Update local state when photos are updated
  const handlePhotosUpdate = (newPhotos: string[]) => {
    updateData({ profile_photos: newPhotos });
  };

  const canProceed = photoCount >= MINIMUM_PHOTOS;
  const photosNeeded = Math.max(0, MINIMUM_PHOTOS - photoCount);

  return (
    <CompleteProfileFooter
      buttonText="Continuar"
      onClick={nextStep}
      disabled={!canProceed}
      isLoading={saving}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 rounded-full bg-luxury-pinkBright/20 flex items-center justify-center mx-auto mb-4">
            <Camera className="w-8 h-8 text-luxury-pinkBright" />
          </div>
          <h2 className="text-2xl font-bold text-white">Suas Fotos</h2>
          <p className="text-white/60 text-sm">
            Adicione pelo menos {MINIMUM_PHOTOS} fotos para completar seu perfil
          </p>
        </div>

        {/* Estado vazio: área grande e clicável para adicionar fotos */}
        {photos.length === 0 && uploadButtonProps && (
          <button
            onClick={uploadButtonProps.onClick}
            disabled={uploadButtonProps.disabled}
            className="w-full aspect-[4/3] flex flex-col items-center justify-center 
                       border-2 border-dashed border-white/30 rounded-xl 
                       bg-white/5 hover:bg-white/10 hover:border-luxury-pinkBright/50 
                       transition-all duration-200 group disabled:opacity-50"
          >
            <div className="w-16 h-16 rounded-full bg-luxury-pinkBright/20 
                            flex items-center justify-center mb-4 
                            group-hover:bg-luxury-pinkBright/30 transition-colors">
              <ImagePlus className="w-8 h-8 text-luxury-pinkBright" />
            </div>
            <span className="text-white font-medium mb-1">Toque para adicionar fotos</span>
            <span className="text-white/50 text-sm">JPG, PNG ou WEBP</span>
          </button>
        )}

        {/* Photo Gallery com grid quando tem fotos */}
        {photos.length > 0 && (
          <PhotoGallery 
            photos={photos} 
            onPhotosUpdate={handlePhotosUpdate}
            addButton={
              uploadButtonProps ? (
                <PhotoUploadButton
                  onClick={uploadButtonProps.onClick}
                  disabled={uploadButtonProps.disabled}
                />
              ) : undefined
            }
          />
        )}

        {/* Photo Uploader - sempre ocultar botão, usamos controle externo */}
        <OptimizedPhotoUploader
          existingPhotos={photos}
          onPhotosUpdate={handlePhotosUpdate}
          hideButton={true}
          onButtonPropsChange={setUploadButtonProps}
        />

        {/* Progress indicator */}
        {!canProceed && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-amber-400">
              <ImagePlus className="w-5 h-5" />
              <span className="text-sm font-medium">
                Adicione mais {photosNeeded} foto{photosNeeded > 1 ? 's' : ''} para continuar
              </span>
            </div>
          </div>
        )}

        {canProceed && (
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
            <p className="text-sm text-emerald-400 font-medium">
              ✓ Você tem {photoCount} fotos - pode continuar!
            </p>
          </div>
        )}

        {/* Encouragement */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <p className="text-sm text-white/70 text-center">
            📸 Perfis com mais fotos recebem <span className="text-luxury-pinkBright font-semibold">5x mais visualizações</span>
          </p>
        </div>
      </div>
    </CompleteProfileFooter>
  );
};

export default PhotosCompleteStep;
